import { useState, type CSSProperties, type FormEvent } from 'react'

export interface LoginGateProps {
  // S and btn() are still untyped legacy helpers — accept them as
  // permissive shapes so the component compiles without rewriting
  // the design system. Wave 17 (ui-kit migration) will tighten these.
  S: Record<string, CSSProperties>
  btn: (variant: string, ...rest: unknown[]) => CSSProperties
  authLoading?: boolean
  authError?: string
  onLogin: (password: string) => Promise<void>
}

// Wave 47z+ — login surface rewrite. The earlier login was a small
// centered card on a black void: brand wordmark + plain h2 + label +
// password input + error + button. Functional, but it didn't echo the
// editorial aesthetic the rest of the studio carries — Fraunces serif
// hero typography, brand-spotlight ambient glow, mono uppercase eyebrow
// tags, hairline-only borders, cast-metal CTAs.
//
// The new login leads with identity (the operator built this; this is
// THEIR command center, not a generic SaaS auth page) and then provides
// a calm, focused access affordance. Everything visible — the strategic
// flow, the version tag, the build status — is the same vocabulary the
// rest of the studio uses, so the login feels like the doorway INTO the
// platform, not a separate system.
export function LoginGate({ authLoading, authError, onLogin }: LoginGateProps) {
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault()
    setSubmitting(true)
    try {
      await onLogin(password)
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="studio-login-shell">
      <div className="studio-login-bg" aria-hidden="true" />
      <div className="studio-login-card">
        <div className="studio-login-eyebrow">
          <span className="studio-login-eyebrow-dot" aria-hidden="true" />
          Command center · v3.4
        </div>

        <h1 className="studio-login-wordmark">
          Ignazio <em>Studio</em>
        </h1>

        <p className="studio-login-tagline">
          The command center for Ignazio De Santis&apos;s body of work. Project
          · Position · Claim · Proof · Usage.
        </p>

        <div className="studio-login-meta">
          <span>@IGNAZIODESANTIS</span>
          <span aria-hidden="true">·</span>
          <span>Built for one operator</span>
          <span aria-hidden="true">·</span>
          <span>Scaled for autonomy</span>
        </div>

        <style>{`
          /* UI elevation 2026-06-09: suppress browser password-reveal glyph */
          .studio-login-input::-webkit-credentials-auto-fill-button,
          .studio-login-input::-ms-reveal {
            display: none !important;
          }
        `}</style>

        <form className="studio-login-form" onSubmit={handleSubmit}>
          <label className="studio-login-field">
            <span className="studio-login-field-label">Studio key</span>
            <input
              autoFocus
              className="studio-login-input"
              type="password"
              value={password}
              placeholder="Enter studio key to unlock"
              onChange={(event) => setPassword(event.target.value)}
              disabled={authLoading || submitting}
              aria-describedby={authError ? 'studio-login-error' : undefined}
            />
          </label>

          {authError ? (
            <div
              className="studio-login-error"
              id="studio-login-error"
              role="alert"
            >
              {authError}
            </div>
          ) : null}

          <button
            type="submit"
            className="studio-login-cta"
            disabled={authLoading || submitting || !password.trim()}
          >
            {submitting || authLoading ? 'Checking access…' : 'Unlock studio'}
          </button>
        </form>

        <div className="studio-login-footer">
          <span className="studio-login-footer-tag">
            Truth · Proof · Authority · Voice
          </span>
        </div>
      </div>
    </div>
  )
}
